import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import {
    DataGridPro,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    GridColDef,
    GridPaginationModel,
    GridRowId,
    GridRowParams,
} from "@mui/x-data-grid-pro";
import { Experiment, ExperimentStatus, useGetExperimentsQuery } from "src/context/api/bidStratTesting/api";
import ExperimentAnalysis from "./experiment-analysis";
import CancelScheduleDialog from "./cancel-schedule-dialog";
import { BlackButton, BlockHeader, CARD_SX, COLORS, GreyButton, Icon, ScheduledBadge, StateMessage } from "./components";
import {
    comparisonPeriodLabel,
    comparisonStrategies,
    comparisonStrategiesText,
    formatDay,
    padCount,
    strategyNotFound,
} from "./utils";
import { tr } from "./i18n";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

// The block never grows past the height of a 10-row page: with more rows per
// page, the grid scrolls inside it, as the other
// grids of the app. A shorter list keeps its own height.
const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 36;
const FOOTER_HEIGHT = 52;
const VISIBLE_ROWS = 10;
const MAX_GRID_HEIGHT = HEADER_HEIGHT + VISIBLE_ROWS * ROW_HEIGHT + FOOTER_HEIGHT;

const ComparisonStrategiesCell = ({ row }: { row: Experiment }) => {
    const strategies = comparisonStrategies(row.strategy_before, row.strategy_after);
    if (!strategies) {
        return <Typography sx={{ fontSize: 14, color: COLORS.muted }}>{strategyNotFound()}</Typography>;
    }
    // Both sides shrink with an ellipsis; the full text is in the tooltip
    return (
        <Tooltip title={`${strategies.reference} ${tr("common.vs")} ${strategies.tested}`} placement="top-start">
            <Stack direction="row" alignItems="center" gap={0.5} width={1} minWidth={0} whiteSpace="nowrap" sx={{ cursor: "help" }}>
                {/* Each side gets at most half of the cell before its ellipsis */}
                <Typography
                    sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.2, color: COLORS.text, minWidth: 0, maxWidth: "50%" }}
                    noWrap
                >
                    {strategies.reference}
                </Typography>
                <Stack
                    direction="row"
                    alignItems="baseline"
                    justifyContent="flex-end"
                    gap={0.5}
                    flex={1}
                    minWidth={0}
                    maxWidth="50%"
                    ml="auto"
                >
                    <Typography component="span" sx={{ fontSize: 11, lineHeight: "12px", color: COLORS.muted, flexShrink: 0 }}>
                        {tr("common.vs")}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.2, color: COLORS.secondary, minWidth: 0 }} noWrap>
                        {strategies.tested}
                    </Typography>
                </Stack>
            </Stack>
        </Tooltip>
    );
};

const COLUMNS: GridColDef<Experiment>[] = [
    {
        field: "name",
        get headerName() {
            return tr("table.title");
        },
        flex: 1,
        minWidth: 180,
        renderCell: ({ value }) => (
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.text }} noWrap>
                {value}
            </Typography>
        ),
    },
    {
        field: "campaign_name",
        get headerName() {
            return tr("table.campaign");
        },
        width: 180,
        renderCell: ({ value }) => (
            <Tooltip title={value} placement="top-start">
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.text }} noWrap>
                    {value}
                </Typography>
            </Tooltip>
        ),
    },
    {
        field: "comparison_strategies",
        get headerName() {
            return tr("table.comparison_strategies");
        },
        width: 340,
        sortable: false,
        valueGetter: (_value, row) => comparisonStrategiesText(row.strategy_before, row.strategy_after),
        renderCell: ({ row }) => <ComparisonStrategiesCell row={row} />,
    },
    {
        field: "start_date",
        get headerName() {
            return tr("table.start_date");
        },
        width: 200,
        valueFormatter: (value) => formatDay(value),
        // Lot 3: badge of a scheduled experiment, switched on its start date
        renderCell: ({ row, formattedValue }) => (
            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                <span>{formattedValue}</span>
                {row.status === "scheduled" && <ScheduledBadge startDate={row.start_date} />}
            </Stack>
        ),
    },
    {
        field: "end_date",
        get headerName() {
            return tr("table.end_date");
        },
        width: 160,
        valueFormatter: (value) => formatDay(value),
    },
    {
        field: "comparison_weeks",
        get headerName() {
            return tr("table.comparison_period");
        },
        width: 160,
        valueFormatter: (value: number) => comparisonPeriodLabel(value),
    },
];

// 38px expand chevron column of the design (MUI default: 50px)
const EXPAND_COLUMN: GridColDef<Experiment> = { ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF, width: 38, minWidth: 38 };
const COMPLETED_WIDTHS: Record<string, Partial<GridColDef<Experiment>>> = {
    start_date: { width: 160 },
    comparison_weeks: { width: 180 },
};

const GRID_SX = {
    border: "none",
    borderRadius: 0,
    "--DataGrid-rowBorderColor": COLORS.border,
    "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": { bgcolor: "#FCFCFD" },
    "& .MuiDataGrid-columnHeader": { borderRight: `1px solid ${COLORS.border}`, px: 1.5 },
    // One line on hover too (sort icon shown): the title is cut with an ellipsis.
    // !important beats data-grid-custom.css, which wraps every header title of the app
    "& .MuiDataGrid-columnHeaderTitle": {
        fontSize: 14,
        fontWeight: 600,
        color: COLORS.secondary,
        whiteSpace: "nowrap !important",
        overflow: "hidden !important",
        textOverflow: "ellipsis",
        lineHeight: "normal !important",
    },
    "& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent": { overflow: "hidden" },
    "& .MuiDataGrid-cell": {
        fontSize: 14,
        // Medium as in Figma: !important beats data-grid-custom.css (font-weight: inherit !important)
        fontWeight: "500 !important",
        color: COLORS.text,
        px: 1.5,
        borderRight: `1px solid ${COLORS.border}`,
        display: "flex",
        alignItems: "center",
    },
    "& .MuiDataGrid-cell--withRenderer, & .MuiDataGrid-detailPanelToggleCell": { borderRight: "none" },
    "& .MuiDataGrid-columnHeader--last, & .MuiDataGrid-cell:last-of-type": { borderRight: "none" },
    // Columns can be resized (as on the other grids): the handle stays invisible,
    // the header border is the separator of the design, and shows on hover
    "& .MuiDataGrid-columnSeparator": { color: "transparent" },
    "& .MuiDataGrid-columnSeparator--resizable:hover": { color: COLORS.muted },
    "& .MuiDataGrid-detailPanel": { bgcolor: "white" },
    // Footer of the other grids of the app (activations/styles/grid.ts): grey labels,
    // bordered "Rows per page" select, grey arrows
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { color: "#919EAB !important" },
    "& .MuiTablePagination-select": {
        borderRadius: "6px",
        border: "1px solid rgba(145, 158, 171, 0.20)",
        color: "#919EAB !important",
    },
    "& .MuiTablePagination-selectIcon": { color: "#919EAB" },
    "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root": { color: "#919EAB !important" },
    "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
        color: "#d0d5da !important",
    },
};

const ExpandIcon = () => <Icon name="chevron-right" size={16} />;
const CollapseIcon = () => <Icon name="chevron-right" size={16} sx={{ transform: "rotate(90deg)" }} />;

interface Props {
    status: ExperimentStatus;
    title: string;
    workspaceId: number;
    regionName: string;
    currency: string;
    locale: string;
    onCreate: () => void;
}

export default function ExperimentsTable({ status, title, workspaceId, regionName, currency, locale, onCreate }: Props) {
    const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

    // Back to the first page when the region changes
    useEffect(() => setPagination((current) => ({ ...current, page: 0 })), [regionName]);

    const { data, isFetching, isError, refetch } = useGetExperimentsQuery({
        workspace_id: workspaceId,
        region_name: regionName,
        status,
        page: pagination.page + 1,
        page_size: pagination.pageSize,
    });

    const total = data?.total ?? 0;
    const rows = useMemo(() => data?.data ?? [], [data]);

    const getDetailPanelContent = useCallback(
        ({ row }: GridRowParams<Experiment>) => <ExperimentAnalysis experiment={row} currency={currency} locale={locale} />,
        [currency, locale]
    );
    const getDetailPanelHeight = useCallback(() => "auto" as const, []);
    // More than 10 rows: the grid scrolls inside the block. An expanded analysis is
    // shown in full, the block grows with it (Lily)
    const [expanded, setExpanded] = useState<GridRowId[]>([]);
    const scrolls = expanded.length === 0 && rows.length > VISIBLE_ROWS;

    const { i18n } = useTranslation();
    const ongoing = status === "running";
    // Lot 3: the trash of a scheduled experiment opens the "Cancel Schedule" confirmation
    const [cancelling, setCancelling] = useState<Experiment | null>(null);
    const columns = useMemo<GridColDef<Experiment>[]>(
        () =>
            ongoing
                ? [
                      EXPAND_COLUMN,
                      ...COLUMNS,
                      {
                          field: "actions",
                          headerName: "--",
                          width: 52,
                          sortable: false,
                          renderCell: ({ row }) =>
                              row.status === "scheduled" ? (
                                  <Tooltip title={tr("table.cancel_schedule")} placement="top">
                                      <IconButton
                                          aria-label={tr("table.cancel_schedule")}
                                          onClick={(event) => {
                                              event.stopPropagation();
                                              setCancelling(row);
                                          }}
                                          sx={{ width: 28, height: 28, p: "6px", borderRadius: "8px" }}
                                      >
                                          <Icon name="trash-04" size={16} />
                                      </IconButton>
                                  </Tooltip>
                              ) : null,
                      },
                  ]
                : // Figma of the completed block: Start date 160px, Comparison period 180px
                  [EXPAND_COLUMN, ...COLUMNS.map((column) => ({ ...column, ...COMPLETED_WIDTHS[column.field] }))],
        // New headers when the language changes: tr() reads it, the rule cannot see it
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ongoing, i18n.language]
    );
    // No "(00)" on an empty block; the empty completed block reads "(0)" (review ticket)
    const countLabel = (count: number | null) => {
        if (count) return ` (${padCount(count)})`;
        return !ongoing && count === 0 ? " (0)" : "";
    };
    const header = (count: number | null) => (
        <BlockHeader
            title={`${title}${countLabel(count)}`}
            action={ongoing && count ? <BlackButton onClick={onCreate}>{tr("table.create_new")}</BlackButton> : undefined}
        />
    );

    if (isError) {
        return (
            <Box sx={CARD_SX}>
                {header(null)}
                <Stack alignItems="center" gap={2} py={4}>
                    <StateMessage title={tr("table.load_error")} description={tr("common.try_again")} />
                    <GreyButton onClick={() => refetch()}>{tr("common.retry")}</GreyButton>
                </Stack>
            </Box>
        );
    }

    if (!data) {
        return <Skeleton variant="rectangular" height={212} sx={{ borderRadius: "12px" }} />;
    }

    if (total === 0) {
        // No completed experiment: the block stays, "Completed experiments (0)" (review ticket)
        if (!ongoing) {
            return (
                <Box sx={CARD_SX}>
                    {header(0)}
                    <Box py={3}>
                        <StateMessage title={tr("table.empty_completed")} />
                    </Box>
                </Box>
            );
        }
        return (
            <Box sx={CARD_SX}>
                {header(0)}
                <Stack alignItems="center" gap={3} py={3}>
                    <StateMessage
                        title={tr("table.empty_title")}
                        description={tr("table.empty_description")}
                    />
                    <GreyButton
                        onClick={onCreate}
                        // Figma: 13px SemiBold, 6px 12px padding, 20% grey background
                        sx={{ fontWeight: 600, py: 0.75, px: 1.5, bgcolor: "rgba(145, 158, 171, 0.20)" }}
                    >
                        {tr("table.empty_create")}
                    </GreyButton>
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={CARD_SX}>
            {header(total)}
            <Box sx={scrolls ? { height: MAX_GRID_HEIGHT } : undefined}>
                <DataGridPro
                    autoHeight={!scrolls}
                    rows={rows}
                    columns={columns}
                    loading={isFetching}
                    rowHeight={ROW_HEIGHT}
                    columnHeaderHeight={HEADER_HEIGHT}
                    rowCount={total}
                    paginationMode="server"
                    pagination
                    paginationModel={pagination}
                    onPaginationModelChange={(model) => {
                        setPagination(model);
                        setExpanded([]);
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={getDetailPanelHeight}
                    detailPanelExpandedRowIds={expanded}
                    onDetailPanelExpandedRowIdsChange={setExpanded}
                    slots={{ detailPanelExpandIcon: ExpandIcon, detailPanelCollapseIcon: CollapseIcon }}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    sx={GRID_SX}
                />
            </Box>
            <CancelScheduleDialog experiment={cancelling} onClose={() => setCancelling(null)} />
        </Box>
    );
}
